import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { Check, ClipboardList, ExternalLink, Loader2, X } from 'lucide-react';
import { ipdApi, type IPDBillEditRequest, type IPDBillEditRequestStatus } from '@/api/ipd';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import IPDPostDischargePharmaRequestsPanel from '@/components/ipd/IPDPostDischargePharmaRequestsPanel';

type StatusFilter = IPDBillEditRequestStatus | 'ALL';

const STATUS_OPTIONS: { value: StatusFilter; label: string }[] = [
  { value: 'ALL', label: 'All' },
  { value: 'PENDING', label: 'Pending' },
  { value: 'APPROVED', label: 'Approved' },
  { value: 'REJECTED', label: 'Rejected' },
];

function statusBadgeClass(status: string): string {
  switch (status) {
    case 'PENDING':
      return 'bg-amber-100 text-amber-800 border-amber-200';
    case 'APPROVED':
      return 'bg-emerald-100 text-emerald-800 border-emerald-200';
    case 'REJECTED':
      return 'bg-red-100 text-red-800 border-red-200';
    default:
      return 'bg-slate-100 text-slate-700 border-slate-200';
  }
}

function formatInr(n: number) {
  return `₹${Number(n).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export default function IPDBillEditRequestsPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('ALL');
  const [reviewDialog, setReviewDialog] = useState<{
    request: IPDBillEditRequest;
    action: 'approve' | 'reject';
  } | null>(null);
  const [reviewNote, setReviewNote] = useState('');

  const queryKey = useMemo(
    () => ['ipd-bill-edit-requests', statusFilter] as const,
    [statusFilter]
  );

  const { data: requests = [], isLoading, isFetching } = useQuery({
    queryKey,
    queryFn: async () => {
      const res = await ipdApi.listBillEditRequests({
        status: statusFilter,
        limit: 200,
      });
      return (res.data?.data ?? []) as IPDBillEditRequest[];
    },
  });

  const pendingCount = useMemo(
    () => requests.filter((r) => r.status === 'PENDING').length,
    [requests]
  );

  const approveMut = useMutation({
    mutationFn: ({ id, adminNote }: { id: string; adminNote?: string }) =>
      ipdApi.approveBillEditRequest(id, adminNote),
    onSuccess: () => {
      toast.success('Approved — receptionist can edit the revenue bill for 48 hours');
      setReviewDialog(null);
      setReviewNote('');
      queryClient.invalidateQueries({ queryKey: ['ipd-bill-edit-requests'] });
    },
    onError: (e: { response?: { data?: { message?: string } } }) =>
      toast.error(e.response?.data?.message || 'Failed to approve'),
  });

  const rejectMut = useMutation({
    mutationFn: ({ id, adminNote }: { id: string; adminNote?: string }) =>
      ipdApi.rejectBillEditRequest(id, adminNote),
    onSuccess: () => {
      toast.success('Request rejected');
      setReviewDialog(null);
      setReviewNote('');
      queryClient.invalidateQueries({ queryKey: ['ipd-bill-edit-requests'] });
    },
    onError: (e: { response?: { data?: { message?: string } } }) =>
      toast.error(e.response?.data?.message || 'Failed to reject'),
  });

  const isReviewPending = approveMut.isPending || rejectMut.isPending;

  const submitReview = () => {
    if (!reviewDialog) return;
    const payload = { id: reviewDialog.request.id, adminNote: reviewNote.trim() || undefined };
    if (reviewDialog.action === 'approve') approveMut.mutate(payload);
    else rejectMut.mutate(payload);
  };

  return (
    <div className="space-y-6 p-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-xl">
            <ClipboardList className="h-5 w-5" />
            IPD approval requests
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue="bill-edit">
            <TabsList>
              <TabsTrigger value="bill-edit">Bill edit (reception)</TabsTrigger>
              <TabsTrigger value="post-discharge-pharma">Post-discharge pharma (Package 1)</TabsTrigger>
            </TabsList>
            <TabsContent value="bill-edit" className="mt-4">
              <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between mb-4">
                <p className="text-sm text-muted-foreground">
                  After discharge, reception cannot change the main revenue IPD bill until you approve a request.
                  Approved edits are allowed for 48 hours.
                </p>
                <div className="flex items-center gap-3 shrink-0">
                  <span className="text-sm text-muted-foreground">Filter</span>
                  <Select
                    value={statusFilter}
                    onValueChange={(v) => setStatusFilter(v as StatusFilter)}
                  >
                    <SelectTrigger className="w-40">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {STATUS_OPTIONS.map((o) => (
                        <SelectItem key={o.value} value={o.value}>
                          {o.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {statusFilter === 'ALL' && pendingCount > 0 && (
                    <Badge variant="outline" className={statusBadgeClass('PENDING')}>
                      {pendingCount} pending in view
                    </Badge>
                  )}
                </div>
              </div>

          {isLoading ? (
            <div className="flex items-center justify-center py-12 text-muted-foreground">
              <Loader2 className="h-6 w-6 animate-spin mr-2" />
              Loading requests…
            </div>
          ) : requests.length === 0 ? (
            <p className="text-sm text-muted-foreground py-8 text-center">
              No {statusFilter === 'ALL' ? '' : statusFilter.toLowerCase() + ' '}edit requests
              found.
            </p>
          ) : (
            <div className="rounded-md border overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Requested</TableHead>
                    <TableHead>Patient</TableHead>
                    <TableHead>IPD / Bill</TableHead>
                    <TableHead>Reason</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Review</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {requests.map((r) => {
                    const patient = r.admission?.queue?.patient;
                    const ipdNum = r.admission?.queue?.ipdNumber ?? '—';
                    return (
                      <TableRow key={r.id}>
                        <TableCell className="whitespace-nowrap text-sm">
                          <div>{format(new Date(r.createdAt), 'dd/MM/yyyy')}</div>
                          <div className="text-xs text-muted-foreground">
                            {format(new Date(r.createdAt), 'h:mm a')}
                          </div>
                          <div className="text-xs text-muted-foreground mt-1">
                            {r.requestedBy?.name ?? '—'}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="font-medium text-sm">{patient?.name ?? '—'}</div>
                          <div className="text-xs text-muted-foreground">{patient?.uhid}</div>
                        </TableCell>
                        <TableCell className="text-sm">
                          <div>IPD {ipdNum}</div>
                          <div className="text-xs text-muted-foreground">
                            {r.ipdBill?.billNumber ?? '—'} · {formatInr(Number(r.ipdBill?.netAmount ?? 0))}
                          </div>
                          <div className="text-xs text-muted-foreground">{r.ipdBill?.status}</div>
                        </TableCell>
                        <TableCell className="max-w-[220px] text-sm">{r.reason}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className={statusBadgeClass(r.status)}>
                            {r.status}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-sm max-w-[180px]">
                          {r.reviewedAt ? (
                            <>
                              <div>{r.reviewedBy?.name ?? 'Admin'}</div>
                              <div className="text-xs text-muted-foreground">
                                {format(new Date(r.reviewedAt), 'dd/MM/yyyy, h:mm a')}
                              </div>
                              {r.adminNote && (
                                <p className="text-xs mt-1 text-muted-foreground">{r.adminNote}</p>
                              )}
                            </>
                          ) : (
                            <span className="text-muted-foreground">—</span>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-1 flex-wrap">
                            {r.status === 'PENDING' && (
                              <>
                                <Button
                                  size="sm"
                                  variant="default"
                                  className="h-8"
                                  onClick={() => {
                                    setReviewNote('');
                                    setReviewDialog({ request: r, action: 'approve' });
                                  }}
                                >
                                  <Check className="h-3 w-3 mr-1" />
                                  Approve
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="h-8"
                                  onClick={() => {
                                    setReviewNote('');
                                    setReviewDialog({ request: r, action: 'reject' });
                                  }}
                                >
                                  <X className="h-3 w-3 mr-1" />
                                  Reject
                                </Button>
                              </>
                            )}
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-8"
                              onClick={() =>
                                navigate(
                                  `/hospital-admin/ipd/${r.admissionId}/billing?billTrack=REVENUE`
                                )
                              }
                            >
                              <ExternalLink className="h-3 w-3 mr-1" />
                              Bill
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
          {isFetching && !isLoading && (
            <p className="text-xs text-muted-foreground mt-2">Refreshing…</p>
          )}
            </TabsContent>
            <TabsContent value="post-discharge-pharma" className="mt-4">
              <IPDPostDischargePharmaRequestsPanel />
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      <Dialog
        open={!!reviewDialog}
        onOpenChange={(open) => {
          if (!open) {
            setReviewDialog(null);
            setReviewNote('');
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {reviewDialog?.action === 'approve' ? 'Approve edit request' : 'Reject edit request'}
            </DialogTitle>
          </DialogHeader>
          {reviewDialog && (
            <div className="space-y-3 text-sm">
              <p>
                <span className="text-muted-foreground">Patient:</span>{' '}
                {reviewDialog.request.admission?.queue?.patient?.name}
              </p>
              <p className="text-muted-foreground">{reviewDialog.request.reason}</p>
              <Textarea
                placeholder="Optional note to receptionist"
                value={reviewNote}
                onChange={(e) => setReviewNote(e.target.value)}
                rows={3}
              />
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setReviewDialog(null)} disabled={isReviewPending}>
              Cancel
            </Button>
            <Button
              variant={reviewDialog?.action === 'reject' ? 'destructive' : 'default'}
              disabled={isReviewPending}
              onClick={submitReview}
            >
              {isReviewPending
                ? 'Saving…'
                : reviewDialog?.action === 'approve'
                  ? 'Approve'
                  : 'Reject'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
